import React, { useEffect, useState } from 'react'

import ripple from '../assets/qr-event/ripple.svg'
import hand from '../assets/qr-event/hand.png'
import listCover from '../assets/qr-event/list-cover.png'
import avatarDefault from '../assets/qr-event/avatar-default.png'
import avatarPhoto from '../assets/qr-event/avatar-photo.png'
import iconCheck from '../assets/qr-event/icon-check.png'
import iconPlus from '../assets/qr-event/icon-plus.png'
import iconBookmark from '../assets/qr-event/icon-bookmark.png'
import iconBookmarkFilled from '../assets/qr-event/icon-bookmark-filled.png'

/**
 * 깐부치킨 매장 QR 이벤트 랜딩 (/qr).
 *
 * 매장 테이블의 QR을 찍으면 여기로 온다. 메뉴나 다른 화면에서는 이 주소로
 * 링크하지 않는다 — QR로만 들어오는 페이지다. 검색에도 안 잡히게 noindex.
 *
 * 이 페이지는 안내와 화면 이동만 한다.
 * - 당첨 여부·남은 치킨 수는 서버만 정한다. 여기엔 확률도 당첨 로직도 없다.
 * - 앱 기능(웹뷰 브리지)을 부르지 않는다. 앱 안인지 알려고 있는지만 본다.
 */

const NOTICE = '계정당 1회 · 소진 시 조기 종료 · 이벤트 기간 11/30까지'

// 스토어 주소는 public/download.html 과 같다.
const APP_STORE_URL = 'https://apps.apple.com/kr/app/id6756393828'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.tastee.app'
// 휴대폰이 아니면(PC 등) 두 스토어를 다 보여주는 안내 페이지로
const DOWNLOAD_PAGE_URL = '/download.html'

const storeUrl = (): string => {
  const ua = navigator.userAgent.toLowerCase()
  if (/iphone|ipad|ipod/.test(ua)) return APP_STORE_URL
  // iPadOS 는 맥처럼 보인다
  if (ua.includes('macintosh') && navigator.maxTouchPoints > 1) return APP_STORE_URL
  if (ua.includes('android')) return PLAY_STORE_URL
  return DOWNLOAD_PAGE_URL
}

/**
 * TASTEE 앱 웹뷰 안인지.
 *
 * platform.ts 의 isApp() 은 webkit.messageHandlers 도 앱으로 보는데, 카카오톡·
 * 인스타 같은 다른 앱의 인앱 브라우저도 그걸 가진다. 그 사람들은 앱이 없으니
 * 스토어로 보내야 한다. 그래서 TASTEE 앱만 넣는 이름 둘만 본다.
 * FlutterChannel 은 문서 시작 때, FlutterBridge 는 로드 뒤에 들어온다.
 */
const hasTasteeBridge = (): boolean => {
  const w = window as any
  return !!(w.FlutterChannel || w.FlutterBridge)
}

const useInTasteeApp = (): boolean => {
  const [inApp, setInApp] = useState(hasTasteeBridge)
  useEffect(() => {
    if (inApp) return
    // FlutterBridge 가 늦게 들어오는 경우를 잠깐 기다린다
    const started = Date.now()
    const timer = setInterval(() => {
      if (hasTasteeBridge()) {
        setInApp(true)
        clearInterval(timer)
      } else if (Date.now() - started > 2000) {
        clearInterval(timer)
      }
    }, 150)
    return () => clearInterval(timer)
  }, [inApp])
  return inApp
}

/** 이 페이지에 있는 동안만 검색 제외 표시를 단다. 다른 페이지엔 영향 없다. */
const useNoIndex = () => {
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    document.head.appendChild(meta)
    return () => {
      meta.remove()
    }
  }, [])
}

const BROWN = 'text-brown-80%-main'

const STEPS: { title: string; lines: string[] }[] = [
  { title: 'TASTEE 앱 설치', lines: ['QR을 통해 앱을 다운받을 수 있어요.'] },
  { title: '가본 매장 5곳 기록하기', lines: ['기록할수록 나의 취향이 보여요.'] },
  {
    title: '룰렛 돌리고 앱에서 당첨 확인',
    lines: [
      'QR을 다시 스캔해서 이벤트 화면으로 이동해요.',
      '당첨 화면을 사장님께 보여주시면 치킨을 드려요.',
    ],
  },
]

const CompareCard: React.FC<{ name: string; area: string; children?: React.ReactNode }> = ({
  name,
  area,
  children,
}) => (
  <div className="relative flex-1 h-[112px] bg-white border-[0.86px] border-[rgba(75,54,33,0.2)] rounded-[14px] shadow-[0_3.5px_4.3px_rgba(74,54,0,0.1)] text-center flex flex-col items-center pt-[35px]">
    <p className={`font-pretendard font-bold text-[14px] leading-[1.25] ${BROWN}`}>{name}</p>
    <p className="font-pretendard font-regular text-[12px] leading-[1.43] text-textColorText3 mt-[8px]">
      {area}
    </p>
    {children}
  </div>
)

const ListRow: React.FC<{ name: string; address: string; icons: string[] }> = ({
  name,
  address,
  icons,
}) => (
  <div className="pt-[14px] h-[62px]">
    <div className="flex items-center justify-between">
      <p className={`font-pretendard font-bold text-[14px] leading-[1.25] tracking-[-0.41px] ${BROWN}`}>
        {name}
      </p>
      <div className="flex items-center gap-[10px] opacity-80">
        {icons.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            className={`size-[21px] object-cover ${i === 0 ? 'rounded-full' : ''}`}
          />
        ))}
      </div>
    </div>
    <p className="mt-[3px] font-pretendard font-regular text-[12px] leading-[17px] tracking-[-0.36px] text-textColorText2">
      {address}
    </p>
  </div>
)

const QrEvent: React.FC = () => {
  useNoIndex()
  const inApp = useInTasteeApp()
  const [soonVisible, setSoonVisible] = useState(false)

  const onAppButton = () => {
    // TODO(룰렛 화면): 룰렛 화면이 생기면 여기서 그 화면으로 이동한다.
    // 당첨 결과는 그 화면이 서버에 물어서 받는다. 여기서 정하지 않는다.
    setSoonVisible(true)
  }

  const buttonClass =
    'block w-full h-[55px] rounded-[28px] bg-brown-80%-main text-[#faf8f3] font-pretendard font-bold text-[16px] leading-[55px] text-center'

  return (
    <div className="app-container min-h-screen bg-white">
      {/* 하단 고정 버튼에 가리지 않게 아래 여백을 둔다 */}
      <div className="pb-[150px]">
        {/* 01 Hero */}
        <section className="px-[24px] pt-[40px] pb-[16px]">
          <h1 className={`font-pretendard font-extrabold text-[28px] leading-[1.5] ${BROWN}`}>
            오늘 먹은 이 치킨,
            <br />내 인생 맛집 중 몇 위일까?
          </h1>
        </section>

        {/* 이벤트 참여 방법 */}
        <section className="pl-[44px] pr-[24px] py-[20px] flex flex-col gap-[20px]">
          <span className="self-start rounded-full bg-brown-80%-main px-[12px] py-[6px] font-pretendard font-bold text-[12px] leading-[1.5] text-[#faf8f3]">
            EVENT
          </span>
          <h2 className={`font-pretendard font-bold text-[22px] leading-[1.5] ${BROWN}`}>
            5곳 기록하고
            <br />
            <span className="text-[#d64545]">깐부치킨 한 마리</span> 받아가자
          </h2>
          {STEPS.map((s, i) => (
            <div key={s.title} className="flex gap-[14px] items-start">
              <div className="shrink-0 size-[32px] rounded-full bg-brown-80%-main flex items-center justify-center font-pretendard font-bold text-[14px] text-white">
                {i + 1}
              </div>
              <div className={`flex-1 flex flex-col gap-[4px] leading-[1.5] ${BROWN}`}>
                <p className="font-pretendard font-bold text-[16px]">{s.title}</p>
                <p className="font-pretendard font-regular text-[13px]">
                  {s.lines.map((l, j) => (
                    <React.Fragment key={j}>
                      {j > 0 && <br />}
                      {l}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            </div>
          ))}
        </section>

        {/* TASTEE 소개 */}
        <section className="px-[24px] pt-[16px]">
          <h2 className={`px-[20px] pb-[10px] font-pretendard font-bold text-[24px] leading-[1.5] ${BROWN}`}>
            TASTEE 소개
          </h2>

          {/* 01 */}
          <div className="px-[20px] pt-[12px] pb-[20px] flex flex-col gap-[8px]">
            <p className={`font-pretendard font-bold text-[16px] leading-[1.5] ${BROWN}`}>01</p>
            <p className="font-pretendard font-bold text-[18px] leading-[1.5] text-[#4b3621]">
              별점 말고 비교하기
            </p>
            <p className={`font-pretendard font-regular text-[14px] leading-[1.5] ${BROWN}`}>
              이상형 월드컵처럼 간편하게 더 만족한 매장 고르기!
            </p>
            <div className="mt-[8px] bg-[#faf8f3] border-[0.86px] border-brown-80%-main rounded-[24px] px-[20px] pt-[20px] pb-[21px]">
              <p className={`font-pretendard font-bold text-[14px] leading-[1.25] ${BROWN}`}>
                어디가 더 좋았어요?
              </p>
              <div className="relative mt-[11px] flex gap-[14px]">
                <CompareCard name="OO치킨" area="서울 중구" />
                <CompareCard name="깐부치킨" area="서울 강동구">
                  <img
                    src={ripple}
                    alt=""
                    width={27.6}
                    height={27.6}
                    className="absolute left-[48px] top-[75px]"
                  />
                  <img
                    src={hand}
                    alt=""
                    className="absolute left-[53px] top-[76px] w-[26px] h-[33px] object-contain -rotate-12"
                  />
                </CompareCard>
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-[35px] rounded-full bg-brown-80%-main flex items-center justify-center font-pretendard font-bold text-[12px] text-white">
                  VS
                </div>
              </div>
            </div>
          </div>

          {/* 02 */}
          <div className="px-[20px] pt-[16px] pb-[20px] flex flex-col gap-[8px]">
            <p className={`font-pretendard font-bold text-[16px] leading-[1.5] ${BROWN}`}>02</p>
            <p className="font-pretendard font-bold text-[18px] leading-[1.5] text-[#4b3621]">
              지도에 저장해둔 맛집, 그대로 가져오기
            </p>
            <p className={`font-pretendard font-regular text-[14px] leading-[1.5] ${BROWN}`}>
              링크만 붙여넣으면 TASTEE로 한 번에 옮겨져요
            </p>
          </div>

          {/* 03 */}
          <div className="px-[20px] pt-[16px] pb-[20px] flex flex-col gap-[8px]">
            <p className={`font-pretendard font-bold text-[16px] leading-[1.5] ${BROWN}`}>03</p>
            <p className="font-pretendard font-bold text-[18px] leading-[1.5] text-[#4b3621]">
              친구,연인과 함께 저장하는 리스트
            </p>
            <p className={`font-pretendard font-regular text-[14px] leading-[1.5] ${BROWN}`}>
              같이 저장하고, 같이 방문하고, 같이 기록하기!
            </p>
            <div className="mt-[8px] bg-[#faf8f3] border-[0.86px] border-brown-80%-main rounded-[19px] shadow-[0_12px_24px_-12px_rgba(69,61,44,0.32)] px-[16px] pt-[15px] pb-[20px]">
              <div className="flex flex-col items-center pb-[15px]">
                {/* 디자인과 같은 자르기: 원본에서 상자+접시 부분만 보인다 */}
                <div className="relative w-[109px] h-[86px] mt-[8px] overflow-hidden">
                  <img
                    src={listCover}
                    alt=""
                    className="absolute max-w-none w-[223.76%] h-[159.87%] left-[-64.49%] top-[-30.2%]"
                  />
                </div>
                <p className={`pt-[8px] font-pretendard font-bold text-[12px] tracking-[-0.2px] ${BROWN}`}>
                  최고의 카페를 찾아서
                </p>
              </div>
              <div className="h-[0.84px] bg-[rgba(69,61,44,0.12)]" />
              {/* 가게 이름·주소는 지어낸 예시다 (실제 상호를 쓰지 않는다) */}
              <ListRow
                name="OO카페"
                address="서울 용산구"
                icons={[avatarDefault, iconCheck, iconBookmark]}
              />
              <ListRow
                name="OO베이커리"
                address="서울 성동구"
                icons={[avatarPhoto, iconPlus, iconBookmarkFilled]}
              />
            </div>
          </div>
        </section>

        {/* 유의사항 */}
        <section className="px-[44px] pt-[8px]">
          <p className="font-pretendard font-bold text-[12px] leading-[1.5] text-textColorText3">
            유의사항
          </p>
          <p className="mt-[4px] font-pretendard font-regular text-[12px] leading-[1.5] text-textColorText3">
            {NOTICE}
          </p>
        </section>
      </div>

      {/* 하단 고정 버튼 */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[390px] bg-white px-[20px] pt-[12px] pb-[calc(16px+env(safe-area-inset-bottom))]">
        {inApp ? (
          <>
            <button type="button" onClick={onAppButton} className={buttonClass}>
              룰렛 돌리러 가기
            </button>
            {soonVisible && (
              <p
                role="status"
                className="mt-[8px] text-center font-pretendard font-regular text-[12px] text-textColorText2"
              >
                곧 열려요! 조금만 기다려 주세요.
              </p>
            )}
          </>
        ) : (
          <>
            <a href={storeUrl()} className={buttonClass}>
              앱 받고 룰렛 돌리기
            </a>
            <p className="mt-[8px] text-center font-pretendard font-regular text-[12px] text-textColorText2">
              설치 후 QR을 한 번 더 찍어 주세요
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default QrEvent
